import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader, Table, Select, Field, Pager } from '../components/ui';
import { api } from '../lib/api';

interface AuditRow {
  id: string;
  actorId: string | null;
  actorEmail: string | null;
  entity: string;
  entityId: string | null;
  action: string;
  summary: string;
  before: unknown;
  after: unknown;
  createdAt: string;
}

function ActionPill({ action }: { action: string }) {
  const color =
    /delete|reject|cancel|freeze/.test(action)
      ? { bg: 'color-mix(in oklab, var(--danger) 14%, transparent)', fg: 'var(--danger)' }
      : /approve|create|mark-paid|surtido|unfreeze|set-permissions/.test(action)
      ? { bg: 'color-mix(in oklab, var(--accent) 14%, transparent)', fg: 'var(--accent)' }
      : { bg: 'var(--surface-2)', fg: 'var(--text-2)' };
  return (
    <span className="mono" style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 999, background: color.bg, color: color.fg, fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
      {action}
    </span>
  );
}

/** Link the entity reference to the most useful detail page when possible. */
function entityHref(entity: string, entityId: string | null): string | null {
  if (!entityId) return null;
  if (entity === 'orders') return `/orders/${entityId}`;
  if (entity === 'transfers') return `/transfers/${entityId}`;
  if (entity === 'auth-files') return `/orders/${entityId}`; // auth-file events use orderId
  if (entity === 'users' || entity === 'wallet') return `/users/${entityId}`;
  if (entity === 'wholesale') return `/wholesale/${entityId}`;
  if (entity === 'stations') return `/stations`;
  if (entity === 'variants') return `/variants`;
  return null;
}

export default function AuditsPage() {
  const [sp, setSp] = useSearchParams();
  const entity = sp.get('entity') ?? '';
  const action = sp.get('action') ?? '';
  const entityId = sp.get('entityId') ?? '';
  const actorEmail = sp.get('actorEmail') ?? '';
  const page = parseInt(sp.get('page') ?? '1', 10);

  const setFilter = (next: Partial<{ entity: string; action: string; entityId: string; actorEmail: string; page: string }>) => {
    const merged: Record<string, string> = {};
    const cur = { entity, action, entityId, actorEmail, page: String(page) };
    for (const k of Object.keys(cur) as (keyof typeof cur)[]) {
      const v = next[k] !== undefined ? next[k] : cur[k];
      if (v && !(k === 'page' && v === '1')) merged[k] = v as string;
    }
    setSp(merged);
  };

  const { data: ents } = useQuery<{ entities: string[] }>({
    queryKey: ['admin', 'audit', 'entities'],
    queryFn: () => api('/api/admin/audit/entities'),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading, isFetching, refetch } = useQuery<{ items: AuditRow[]; total: number; page: number; limit: number }>({
    queryKey: ['admin', 'audit', entity, action, entityId, actorEmail, page],
    queryFn: () => api('/api/admin/audit', { query: { entity: entity || undefined, action: action || undefined, entityId: entityId || undefined, actorEmail: actorEmail || undefined, page } }),
  });

  const entityOptions = [{ value: '', label: 'Todas' }, ...(ents?.entities ?? []).map((e) => ({ value: e, label: e }))];

  return (
    <div>
      <PageHeader
        kicker="OBSERVABILIDAD"
        title={`Auditoría · ${data?.total ?? 0}`}
        sub="Registro inmutable de toda mutación admin/operator. Retención: 18 meses."
        action={
          <button onClick={() => refetch()} disabled={isFetching} style={{ background: 'transparent', border: '1px solid var(--border-2)', color: 'var(--text-2)', padding: '8px 14px', borderRadius: 10, fontSize: 12, cursor: isFetching ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
            {isFetching ? 'Actualizando…' : 'Refrescar'}
          </button>
        }
      />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200 }}>
          <Select<string> label="Entidad" value={entity} onChange={(v) => setFilter({ entity: v, page: '1' })} options={entityOptions} />
        </div>
        <div style={{ minWidth: 200 }}>
          <Field label="Acción" value={action} onChange={(v) => setFilter({ action: v, page: '1' })} placeholder="approve, mark-paid…" />
        </div>
        <div style={{ minWidth: 220 }}>
          <Field label="Entity ID" value={entityId} onChange={(v) => setFilter({ entityId: v, page: '1' })} placeholder="TC-…, CKT-…, ObjectId" />
        </div>
        <div style={{ minWidth: 220, flex: 1 }}>
          <Field label="Actor email" value={actorEmail} onChange={(v) => setFilter({ actorEmail: v, page: '1' })} placeholder="email exacto" />
        </div>
      </div>

      <Table<AuditRow>
        rows={data?.items ?? []}
        empty={isLoading ? 'Cargando…' : 'Sin eventos auditados.'}
        onRowClick={(r) => { window.location.href = `/audits/${r.id}`; }}
        columns={[
          {
            key: 'created',
            label: 'Fecha',
            width: 160,
            render: (r) => (
              <Link to={`/audits/${r.id}`} className="mono" style={{ color: 'var(--text)', textDecoration: 'none', fontSize: 12 }}>
                {new Date(r.createdAt).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </Link>
            ),
          },
          { key: 'actor', label: 'Actor', render: (r) => <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.actorEmail ?? '—'}</span> },
          { key: 'entity', label: 'Entidad', render: (r) => <span className="mono" style={{ fontSize: 11 }}>{r.entity}</span>, width: 120 },
          { key: 'action', label: 'Acción', render: (r) => <ActionPill action={r.action} />, width: 130 },
          {
            key: 'entityId',
            label: 'ID',
            render: (r) => {
              if (!r.entityId) return <span style={{ color: 'var(--text-3)' }}>—</span>;
              const href = entityHref(r.entity, r.entityId);
              return href ? (
                <Link to={href} className="mono" style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: 11 }} onClick={(e) => e.stopPropagation()}>
                  {r.entityId.length > 24 ? r.entityId.slice(0, 8) + '…' + r.entityId.slice(-6) : r.entityId} →
                </Link>
              ) : (
                <span className="mono" style={{ fontSize: 11 }}>{r.entityId}</span>
              );
            },
          },
          { key: 'summary', label: 'Resumen', render: (r) => <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.summary || '—'}</span> },
        ]}
      />

      {data && <Pager total={data.total} page={data.page} limit={data.limit} onPage={(p) => setFilter({ page: String(p) })} />}
    </div>
  );
}
